import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import Select from "../../../shared/ui/Field/Select";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
  memberDisplayName,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";

import {
  assignCompanyProperty,
  fetchCompanyPropertiesUnassigned,
} from "../api/companyProperties";
import {
  dealTypeLabel,
  formatPropertyPrice,
  isCorporatePropertiesAvailable,
  propertyDisplayTitle,
  propertyStatusLabel,
  propertyTypeLabel,
} from "../lib/propertyLabels";

function unwrapPage(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    count: typeof data?.count === "number" ? data.count : 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function CompanyPropertiesUnassignedContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);

  const [page, setPage] = useState(1);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [rows, setRows] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [assignDraft, setAssignDraft] = useState({});

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const canManage =
    authorityMatches && isAdminMembership(company, membership);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const keepVisible =
      background &&
      (stateRef.current === "ready" || stateRef.current === "unavailable");
    const op = scopeGate.beginRead({ companyId, actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
      setActionError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      if (!mineCompanyMatches || !isActiveMembership(companyData, mineMatches)) {
        setRows([]);
        setAssignees([]);
        setState("denied");
        return;
      }
      if (!isAdminMembership(companyData, mineMatches)) {
        setRows([]);
        setAssignees([]);
        setState("denied");
        return;
      }
      if (!isCorporatePropertiesAvailable(companyData)) {
        setRows([]);
        setAssignees([]);
        setState("unavailable");
        return;
      }

      const [pageData, roster] = await Promise.all([
        fetchCompanyPropertiesUnassigned(companyId, {
          page,
          page_size: 20,
        }),
        fetchMembers(companyId, "active"),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const unwrapped = unwrapPage(pageData);
      setRows(unwrapped.results);
      setCount(unwrapped.count);
      setNextUrl(unwrapped.next);
      setPrevUrl(unwrapped.previous);
      setAssignees(
        (Array.isArray(roster) ? roster : []).filter(
          (m) =>
            m.status === "active" &&
            (m.role === "member" || m.role === "admin"),
        ),
      );
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      setRows([]);
      setAssignees([]);
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, companyId, page, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyOpRef.current = null;
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setRows([]);
    setAssignDraft({});
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [companyId, actorId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" || state === "unavailable",
    busy: busyId != null,
    onRevalidate: () => load({ background: true }),
  });

  const handleAssign = async (propertyId) => {
    if (!canManage || busyOpRef.current || writesSuspendedRef.current) return;
    const userId = assignDraft[propertyId];
    if (!userId) {
      setActionError("Оберіть відповідального агента.");
      return;
    }
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: propertyId,
    });
    busyOpRef.current = op;
    setBusyId(propertyId);
    setActionError(null);
    try {
      await assignCompanyProperty(companyId, propertyId, Number(userId));
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setRows((prev) => prev.filter((row) => String(row.id) !== String(propertyId)));
      setAssignDraft((prev) => {
        const next = { ...prev };
        delete next[propertyId];
        return next;
      });
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setRows((prev) =>
          prev.filter((row) => String(row.id) !== String(propertyId)),
        );
        if (status === 403) setState("denied");
        return;
      }
      setActionError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusyId(null);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(count / 20) || 1);
  const assigneeOptions = [
    { value: "", label: "Оберіть агента" },
    ...assignees.map((m) => ({
      value: String(m.user?.id ?? m.user_id ?? ""),
      label: memberDisplayName(m),
    })),
  ];

  return (
    <section
      className="clients-page feature-company-workspace feature-company-properties"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
        <CompanyPageHeader
          title="Непризначені об'єкти"
          subtitle="Черга для OWNER/ADMIN · Компанія"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties-unassigned"
          actions={
            <Link
              to={`/companies/${companyId}/properties`}
              className="action-btn action-btn--ghost"
            >
              До списку
            </Link>
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "unavailable" ? (
          <EmptyState
            title="Корпоративні об'єкти недоступні"
            description="Операції з об'єктами компанії вимкнені."
          />
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Черга непризначених об'єктів доступна лише активним OWNER/ADMIN."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Компанію або ресурс не знайдено."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={error || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canManage ? (
          <div className="clients-workspace">
            {actionError ? (
              <p className="feature-company-workspace__error" role="alert">
                {actionError}
              </p>
            ) : null}
            {rows.length === 0 ? (
              <EmptyState
                title="Черга порожня"
                description="Усі об'єкти компанії мають відповідального або інвентар порожній."
              />
            ) : (
              <>
                <div className="clients-table-wrap" role="region" aria-label="Непризначені об'єкти">
                  <table className="clients-table">
                    <thead>
                      <tr>
                        <th scope="col">Назва</th>
                        <th scope="col">Тип</th>
                        <th scope="col">Угода</th>
                        <th scope="col">Ціна</th>
                        <th scope="col">Статус</th>
                        <th scope="col">Призначити</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td data-label="Назва">
                            <Link
                              to={`/companies/${companyId}/properties/${row.id}`}
                            >
                              {propertyDisplayTitle(row)}
                            </Link>
                          </td>
                          <td data-label="Тип">
                            {propertyTypeLabel(row.property_type)}
                          </td>
                          <td data-label="Угода">
                            {dealTypeLabel(row.deal_type)}
                          </td>
                          <td data-label="Ціна">{formatPropertyPrice(row)}</td>
                          <td data-label="Статус">
                            {propertyStatusLabel(row.status)}
                          </td>
                          <td data-label="Призначити">
                            <div className="clients-toolbar__row">
                              <Select
                                aria-label={`Відповідальний для ${propertyDisplayTitle(row)}`}
                                value={assignDraft[row.id] || ""}
                                onChange={(e) =>
                                  setAssignDraft((prev) => ({
                                    ...prev,
                                    [row.id]: e.target.value,
                                  }))
                                }
                                options={assigneeOptions}
                                disabled={busyId === row.id}
                              />
                              <button
                                type="button"
                                className="action-btn"
                                disabled={busyId === row.id}
                                aria-busy={busyId === row.id}
                                onClick={() => handleAssign(row.id)}
                              >
                                Призначити
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <NumberedPagination
                  currentPage={page}
                  totalPages={totalPages}
                  hasNext={Boolean(nextUrl)}
                  hasPrevious={Boolean(prevUrl)}
                  ariaLabel="Сторінки непризначених об'єктів"
                  onPageChange={setPage}
                />
              </>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default CompanyPropertiesUnassignedContent;
